import { useState, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';
import {
  advancedApprovalGroupListSelector,
  allApprovalUsersNotApproversSelector,
  approvalGroupListSelector,
  approversSelector,
} from 'domain/approvals/selectors';
import { organizationUsersForConfidentialDocSelector } from 'domain/organizationUser/selectors';
import { UserEnhancedApprovalFactory } from 'domain/approvals/helpers';
import { createFilterOptions } from '@mui/material/Autocomplete';

export const DEFAULT_GROUP = 'all';

export const messages = {
  [DEFAULT_GROUP]: {
    id: 'document.approval.all',
    defaultMessage: 'All',
  },
  accountants: {
    id: 'document.approval.accountants',
    defaultMessage: 'Accountants',
  },
  clients: { id: 'document.approval.clients', defaultMessage: 'Clients' },
  groups: {
    id: 'document.approval.groups',
    defaultMessage: 'Basic groups',
  },
  advancedGroups: {
    id: 'document.approval.advancedGroups',
    defaultMessage: 'Advanced approval groups',
  },
  inactiveUsers: {
    id: 'document.approval.notPartOfTheApproversList',
    defaultMessage: 'Users not part of the approvers list',
    hintText: {
      id: 'document.approval.notPartOfTheApproversList.hintText',
      defaultMessage:
        'These users are not part of the approvers list and cannot be selected. Please contact your administrator to add them.',
    },
  },
};

const GroupFactory = (group, users, groupName) => {
  const g = group.toJS();
  return {
    ...g,
    approvers: g.approvers.map((a) => users.find((u) => u.id === a)).filter(Boolean),
    group: groupName,
  };
};

const AdvancedGroupFactory = (group, users, groupName) => {
  const g = group.toJS();
  return {
    ...g,
    levels: g.levels
      .map((l) => ({ ...l, approvers: l.approvers.map((a) => users.find((u) => u.id === a)).filter(Boolean) }))
      .filter(Boolean),
    group: groupName,
  };
};

const mapStateToProps = (state) => ({
  approvalGroups: approvalGroupListSelector(state),
  advancedApprovalGroups: advancedApprovalGroupListSelector(state),
  companyApprovers: approversSelector(state),
  organizationUsers: organizationUsersForConfidentialDocSelector(state),
  allApprovalUsersNotApprovers: allApprovalUsersNotApproversSelector(state),
});

const [values, order] = [[], ['groups', 'advancedGroups', 'clients', 'accountants', 'inactiveUsers']];

export default (args = {}) => {
  const { formValue = values, groupsOrder = order, isApproversList = true, withInactiveUsers = true } = args;

  const [approvers, setApprovers] = useState([]);
  const { organizationUsers, approvalGroups, companyApprovers, advancedApprovalGroups, allApprovalUsersNotApprovers } =
    useSelector(mapStateToProps);

  const users = isApproversList ? companyApprovers : organizationUsers;

  useEffect(() => {
    const callbacks = {
      groups: (i) => i.group_id,
      advancedGroups: (i) => i.levels,
      accountants: (i) => ['accountant', 'limited-accountant'].includes(i.role),
      clients: (i) => !['accountant', 'limited-accountant'].includes(i.role),
    };

    const list = [];

    users.forEach((u) => {
      // eslint-disable-next-line no-unused-vars
      const [key] = Object.entries(callbacks).find(([_, v]) => v(u)) || [];
      if (key) {
        const approver = {
          ...UserEnhancedApprovalFactory({
            userGuid: isApproversList ? u.username : u.userId,
            tag: isApproversList ? u.username : u.userId,
            id: isApproversList ? u.id : u.userId,
            picture: u.picture,
            fullName: isApproversList ? u.fullName : u.username,
          }).toJS(),
          group: key,
        };

        list.push(approver);
      }
    });

    if (withInactiveUsers) {
      allApprovalUsersNotApprovers?.forEach((u) => {
        const isDuplicate = list.some((l) => l.userGuid === u.username || l.tag === u.username || l.id === u.id);

        if (!isDuplicate) {
          const inactiveApprover = {
            ...UserEnhancedApprovalFactory({
              userGuid: u.username,
              tag: u.username,
              id: u.id,
              picture: u.picture,
              fullName: u.fullName,
            }).toJS(),
            group: 'inactiveUsers',
            disabled: true,
          };

          list.push(inactiveApprover);
        }
      });
    }

    const groups = [];
    approvalGroups.forEach((g) => {
      // eslint-disable-next-line no-unused-vars
      const [key] = Object.entries(callbacks).find(([_, v]) => v(g)) || [];
      const group = GroupFactory(g, list, key);
      groups.push(group);
    });

    const advancedGroups = [];
    advancedApprovalGroups.forEach((g) => {
      // eslint-disable-next-line no-unused-vars
      const [key] = Object.entries(callbacks).find(([_, v]) => v(g)) || [];
      const group = AdvancedGroupFactory(g, list, key);
      advancedGroups.push(group);
    });

    setApprovers(
      [
        ...list.sort((a, b) => a.fullName.localeCompare(b.fullName)),
        ...groups.sort((a, b) => a.group_title.localeCompare(b.group_title)),
        ...advancedGroups.sort((a, b) => a.name.localeCompare(b.name)),
      ]
        .filter((i) => groupsOrder.includes(i.group))
        .sort((a, b) => groupsOrder.indexOf(a.group) - groupsOrder.indexOf(b.group)),
    );
  }, [
    users,
    isApproversList,
    groupsOrder,
    approvalGroups,
    advancedApprovalGroups,
    allApprovalUsersNotApprovers,
    withInactiveUsers,
  ]);

  const value = useMemo(
    () =>
      approvers
        .filter((a) => formValue.some((f) => f.id === a.id))
        .sort((a, b) => formValue.findIndex((v) => v.id === a.id) - formValue.findIndex((v) => v.id === b.id)),
    [approvers, formValue],
  );

  return { approvers, value };
};

export const filterOptions = createFilterOptions({
  stringify: (option) => {
    if (option.levels) {
      return `${option.name} ${option.levels
        .flatMap((level) => level.approvers)
        .map((approver) => `${approver.fullName} ${approver.tag}`)
        .join(' ')}`;
    }

    if (option.group_id) {
      return `${option.group_title} ${option.approvers.map((o) => `${o.fullName} ${o.tag}`).join(' ')}`;
    }

    return `${option.fullName} ${option.tag}`;
  },
});
